import { Brain, KeyRound, MessageSquare, Shield, Sparkles, Zap } from "lucide-react";

type AgentFeature = {
  icon: typeof Shield;
  title: string;
  body: string;
  /** Renders a `coming soon` chip next to the title. */
  soon?: boolean;
};

const AGENT_FEATURES: AgentFeature[] = [
  {
    icon: Shield,
    title: "Isolated by default",
    body: "Each Boo runs in its own sandbox, fully walled off from your machine and from other Boos. Files, secrets, and processes don't bleed across.",
  },
  {
    icon: Zap,
    title: "Fully capable",
    body: "Backed by Claude with full tool access. Reads and writes files, runs code, browses the web, ships real work end to end.",
  },
  {
    icon: Brain,
    title: "Persistent memory",
    body: "Each Boo remembers context across conversations and keeps its own workspace. Files and decisions carry over, every session.",
  },
  {
    icon: Sparkles,
    title: "Skills, built in or yours",
    body: "Comes with skills out of the box and can pick up new ones you author. They level up the more you use them.",
  },
  {
    icon: MessageSquare,
    title: "Talk to it your way",
    body: "Chat in the dashboard, or connect Slack and DM your Boo from your team workspace. Same memory, same workspace, wherever you reach it.",
  },
  {
    icon: KeyRound,
    title: "Vault",
    body: "Store credentials once at the org level, override per Boo when needed. It reads what it needs at runtime — secrets stay out of chat and logs.",
  },
];

/**
 * Banner of feature highlights — six cards in a responsive 3-col grid sitting
 * on a soft wash. Borderless throughout; cards differentiate from the wash via
 * background contrast alone. Used on the dashboard behind a "Learn more"
 * toggle, but reusable on its own for marketing surfaces.
 */
export function AgentHighlights() {
  return (
    <div className="rounded-xl bg-muted/40 p-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {AGENT_FEATURES.map(({ icon: Icon, title, body, soon }) => (
          <div key={title} className="space-y-2 rounded-lg bg-card p-4">
            <div className="flex items-center gap-2.5">
              <div className="flex size-8 items-center justify-center rounded-full bg-muted">
                <Icon className="size-4 text-foreground" />
              </div>
              <h3 className="text-sm font-semibold">
                {title}
                {soon && (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    coming soon
                  </span>
                )}
              </h3>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {body}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
